import { useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { ApiError } from '../../../shared/shared.api';
import { createAccountingPeriod } from '../accounting-periods.api';

/**
 * Adding a period. Normally it follows the latest one and a failure toasts; with no period yet the first needs a start
 * date, asked for in a dialog that shows the server's refusal inline.
 */
export function useAddPeriod(options: { hasNoPeriods: boolean; reload: () => Promise<void> }) {
  const { hasNoPeriods, reload } = options;
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  async function create(startDate?: string): Promise<boolean> {
    setError(null);
    try {
      const period = await createAccountingPeriod(startDate ? { startDate } : {});
      await reload();
      toast.success(`Period ${period.startDate} to ${period.endDate} added`);
      return true;
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 409 ? 'The periods have changed since the page loaded. Reload and try again.' : "Couldn't add the period";
      if (!startDate) toast.error(message);
      else setError(err instanceof ApiError && err.status === 400 ? (apiErrorMessage(err) ?? message) : message);
      return false;
    }
  }

  function start() {
    if (!hasNoPeriods) {
      void create();
      return;
    }
    setError(null);
    setDialogOpen(true);
  }

  return { start, create, error, isDialogOpen, closeDialog: () => setDialogOpen(false) };
}
